"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { MapPin, Car, Bus, AlertTriangle, ExternalLink, LogOut, User } from "lucide-react"
import { Suspense } from "react"
import ImageCarousel from "@/components/image-carousel"
import { usePhoneAuth } from "@/components/phone-auth-context"
import PhoneAuthModal from "@/components/phone-auth-modal"

interface SiteInfo {
  nome_noiva?: string
  nome_noivo?: string
  corPrimaria: string
  corSecundaria: string
  corTerciaria: string
  corQuaternaria?: string
  dressCodeTitulo: string
  dressCodeDescricao: string
  dressCodeHomem: string
  dressCodeMulher: string
  localCerimonia?: string
  enderecoCerimonia?: string
  textoCerimonia?: string
  linkMaps?: string
  localRecepcao?: string
  enderecoRecepcao?: string
  textoRecepcao?: string
  linkMapsRecepcao?: string
  mesmoLocal: boolean
}

interface InformacoesClientProps {
  initialSiteInfo: SiteInfo
  venueImages: string[]
}

export default function InformacoesClient({ initialSiteInfo, venueImages }: InformacoesClientProps) {
  const { isAuthenticated, guestData, logout, authenticateWithGuest } = usePhoneAuth()
  const [siteInfo, setSiteInfo] = useState<SiteInfo>(initialSiteInfo)

  useEffect(() => {
    if (initialSiteInfo) {
      setSiteInfo(initialSiteInfo)
    }
  }, [initialSiteInfo])

  // Função para renderizar a seção de local com base nas configurações
  const renderLocationSection = () => {
    if (siteInfo.mesmoLocal) {
      // Se for tudo no mesmo lugar
      return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-2xl font-semibold mb-4" style={{ color: siteInfo.corSecundaria }}>
              Local do Evento
            </h3>
            <p className="mb-4" style={{ color: siteInfo.corTerciaria }}>
              <strong>{siteInfo.localCerimonia || "Local a confirmar"}</strong>
            </p>
            {siteInfo.enderecoCerimonia && (
              <p className="mb-4" style={{ color: siteInfo.corTerciaria }}>
                {siteInfo.enderecoCerimonia}
              </p>
            )}
            {siteInfo.textoCerimonia && (
              <p className="mb-6" style={{ color: siteInfo.corTerciaria, whiteSpace: "pre-line" }}>
                {siteInfo.textoCerimonia}
              </p>
            )}
            {siteInfo.linkMaps && (
              <a href={siteInfo.linkMaps} target="_blank" rel="noopener noreferrer">
                <Button
                  className="flex items-center gap-2 mb-8"
                  style={{ backgroundColor: siteInfo.corPrimaria, color: "#FFFFFF" }}
                >
                  <MapPin size={18} />
                  Ver no Google Maps
                </Button>
              </a>
            )}
          </div>
          <div className="rounded-lg overflow-hidden shadow-lg h-[400px]">
            <Suspense fallback={<div className="w-full h-full bg-gray-200 animate-pulse" />}>
              <ImageCarousel
                images={venueImages.length > 0 ? venueImages : ["/elegant-wedding-venue.png"]}
                autoplay={true}
                interval={5000}
                className="h-full"
              />
            </Suspense>
          </div>
        </div>
      )
    } else {
      // Se a cerimônia e recepção forem em locais diferentes
      return (
        <div className="space-y-12">
          {/* Cerimônia */}
          {siteInfo.localCerimonia && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div>
                <h3 className="text-2xl font-semibold mb-4" style={{ color: siteInfo.corSecundaria }}>
                  Local da Cerimônia
                </h3>
                <p className="mb-4" style={{ color: siteInfo.corTerciaria }}>
                  <strong>{siteInfo.localCerimonia}</strong>
                </p>
                {siteInfo.enderecoCerimonia && (
                  <p className="mb-4" style={{ color: siteInfo.corTerciaria }}>
                    {siteInfo.enderecoCerimonia}
                  </p>
                )}
                {siteInfo.textoCerimonia && (
                  <p className="mb-6" style={{ color: siteInfo.corTerciaria, whiteSpace: "pre-line" }}>
                    {siteInfo.textoCerimonia}
                  </p>
                )}
                {siteInfo.linkMaps && (
                  <a href={siteInfo.linkMaps} target="_blank" rel="noopener noreferrer">
                    <Button
                      className="flex items-center gap-2 mb-8"
                      style={{ backgroundColor: siteInfo.corPrimaria, color: "#FFFFFF" }}
                    >
                      <MapPin size={18} />
                      Ver no Google Maps
                    </Button>
                  </a>
                )}
              </div>
              <div className="rounded-lg overflow-hidden shadow-lg h-[350px]">
                <Suspense fallback={<div className="w-full h-full bg-gray-200 animate-pulse" />}>
                  <div className="relative w-full h-full">
                    <Image
                      src={venueImages[0] || "/elegant-wedding-venue.png"}
                      alt="Local da cerimônia"
                      fill
                      className="object-cover"
                    />
                  </div>
                </Suspense>
              </div>
            </div>
          )}

          {/* Recepção */}
          {siteInfo.localRecepcao && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div>
                <h3 className="text-2xl font-semibold mb-4" style={{ color: siteInfo.corSecundaria }}>
                  Local da Recepção
                </h3>
                <p className="mb-4" style={{ color: siteInfo.corTerciaria }}>
                  <strong>{siteInfo.localRecepcao}</strong>
                </p>
                {siteInfo.enderecoRecepcao && (
                  <p className="mb-4" style={{ color: siteInfo.corTerciaria }}>
                    {siteInfo.enderecoRecepcao}
                  </p>
                )}
                {siteInfo.textoRecepcao && (
                  <p className="mb-6" style={{ color: siteInfo.corTerciaria, whiteSpace: "pre-line" }}>
                    {siteInfo.textoRecepcao}
                  </p>
                )}
                {siteInfo.linkMapsRecepcao && (
                  <a href={siteInfo.linkMapsRecepcao} target="_blank" rel="noopener noreferrer">
                    <Button
                      className="flex items-center gap-2 mb-8"
                      style={{ backgroundColor: siteInfo.corPrimaria, color: "#FFFFFF" }}
                    >
                      <MapPin size={18} />
                      Ver no Google Maps
                    </Button>
                  </a>
                )}
              </div>
              <div className="rounded-lg overflow-hidden shadow-lg h-[350px]">
                <Suspense fallback={<div className="w-full h-full bg-gray-200 animate-pulse" />}>
                  <div className="relative w-full h-full">
                    <Image
                      src={venueImages[1] || venueImages[0] || "/elegant-wedding-reception.png"}
                      alt="Local da recepção"
                      fill
                      className="object-cover"
                    />
                  </div>
                </Suspense>
              </div>
            </div>
          )}

          {/* Mensagem se nenhum local estiver definido */}
          {!siteInfo.localCerimonia && !siteInfo.localRecepcao && (
            <div className="text-center py-8">
              <p className="text-xl" style={{ color: siteInfo.corTerciaria }}>
                Locais a confirmar
              </p>
            </div>
          )}
        </div>
      )
    }
  }

  // Função para determinar qual endereço usar para as direções
  const getDirectionsAddress = () => {
    if (siteInfo.mesmoLocal || !siteInfo.localRecepcao) {
      return siteInfo.enderecoCerimonia || siteInfo.localCerimonia
    } else {
      // Se tiver ambos, prioriza a recepção para as direções
      return siteInfo.enderecoRecepcao || siteInfo.localRecepcao
    }
  }

  // Função para determinar qual link do Maps usar
  const getMapsLink = () => {
    if (siteInfo.mesmoLocal || !siteInfo.linkMapsRecepcao) {
      return siteInfo.linkMaps
    } else {
      // Se tiver ambos, prioriza a recepção para o link
      return siteInfo.linkMapsRecepcao
    }
  }

  // Se não estiver autenticado, mostrar modal de autenticação
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white">
        <PhoneAuthModal
          isOpen={true}
          onSuccess={(guest) => {
            // Usar o contexto para autenticar diretamente com os dados do guest
            authenticateWithGuest(guest)
          }}
        />
        <div className="container mx-auto py-16 px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-playfair mb-6" style={{ color: siteInfo.corSecundaria }}>
            Informações
          </h1>
          <p className="text-lg" style={{ color: siteInfo.corTerciaria }}>
            Por favor, autentique-se para visualizar as informações do evento.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Cabeçalho com informações do usuário */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg" style={{ backgroundColor: `${siteInfo.corPrimaria}15` }}>
                <User className="h-5 w-5" style={{ color: siteInfo.corPrimaria }} />
                <span className="font-medium" style={{ color: siteInfo.corSecundaria }}>
                  {guestData?.nome || "Convidado"}
                </span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="flex items-center gap-2"
              style={{ borderColor: siteInfo.corPrimaria, color: siteInfo.corPrimaria }}
            >
              <LogOut className="h-4 w-4" />
              Sair
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto py-16 px-4">
        <h1 className="text-4xl md:text-5xl font-playfair text-center mb-12" style={{ color: siteInfo.corSecundaria }}>
          Informações
        </h1>

        {/* Dress Code - Acima da seção de local */}
        <section className="mb-16">
          <h2 className="text-3xl font-playfair text-center mb-8" style={{ color: siteInfo.corSecundaria }}>
            Dress Code
          </h2>
          <div className="max-w-4xl mx-auto">
            <Card className="border shadow-md" style={{ borderColor: siteInfo.corTerciaria }}>
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl" style={{ color: siteInfo.corSecundaria }}>
                  {siteInfo.dressCodeTitulo}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-center mb-8" style={{ color: siteInfo.corTerciaria, whiteSpace: "pre-line" }}>
                  {siteInfo.dressCodeDescricao}
                </p>

                <div className="flex flex-col md:flex-row justify-center gap-8 md:gap-16">
                  <div className="flex flex-col items-center">
                    {/* Ícone de terno para homens - usando o SVG fornecido */}
                    <svg width="64" height="64" viewBox="0 0 512 512" className="mb-4">
                      <g fill={siteInfo.corSecundaria}>
                        <path
                          d="M511.735,411.733l-64.819-288.538c-4.356-19.391-17.332-35.718-35.238-44.346l-72.443-34.884l-83.231,213.436
                          l-83.24-213.436l-72.442,34.884c-17.906,8.628-30.882,24.955-35.238,44.346L0.265,411.733c-1.309,5.842,2.356,11.633,8.189,12.959
                          l53.134,11.996c5.766,1.3,11.507-2.262,12.917-8.003l47.665-195.183l14.335,95.6l-25.64,119.668
                          c-0.785,3.672-0.025,7.497,2.127,10.578c2.136,3.073,5.454,5.133,9.169,5.665l117.11,17.036l16.732-46.474l16.724,46.474
                          l117.11-17.036c3.707-0.532,7.032-2.592,9.169-5.665c2.152-3.081,2.912-6.906,2.127-10.578l-25.638-119.668l14.335-95.6
                          l47.664,195.183c1.401,5.741,7.151,9.304,12.917,8.003l53.135-11.996C509.38,423.366,513.044,417.575,511.735,411.733z
                          M256.004,394.418c-5.598,0-10.131-4.542-10.131-10.131c0-5.597,4.533-10.131,10.131-10.131c5.589,0,10.122,4.534,10.122,10.131
                          C266.126,389.876,261.593,394.418,256.004,394.418z M256.004,325.91c-5.598,0-10.131-4.534-10.131-10.131
                          c0-5.589,4.533-10.122,10.131-10.122c5.589,0,10.122,4.533,10.122,10.122C266.126,321.377,261.593,325.91,256.004,325.91z"
                        />
                        <polygon points="270.348,65.535 273.53,53.075 267.874,29.952 243.156,29.952 237.491,53.075 240.682,65.535" />
                        <polygon points="275.861,175.343 271.943,83.408 240.057,83.408 236.14,175.343 256.004,227.111" />
                      </g>
                    </svg>
                    <h3 className="text-xl font-medium mb-2" style={{ color: siteInfo.corSecundaria }}>
                      Homens
                    </h3>
                    <p className="text-center" style={{ color: siteInfo.corTerciaria, whiteSpace: "pre-line" }}>
                      {siteInfo.dressCodeHomem}
                    </p>
                  </div>

                  <div className="flex flex-col items-center">
                    {/* Ícone de vestido para mulheres - usando o SVG fornecido */}
                    <svg width="64" height="64" viewBox="0 0 36 36" className="mb-4">
                      <path
                        fill={siteInfo.corPrimaria}
                        d="M22.42 11.847L26 2s-.398.002-1 .034V0h-2v2.219c-2.271.302-5 1.028-5 2.781c0-1.753-2.729-2.479-5-2.781V0h-2v2.034C10.398 2.002 10 2 10 2l3.581 9.847L1 25.643S7 36 18 36s17-10.357 17-10.357L22.42 11.847z"
                      />
                      <path fill={siteInfo.corPrimaria} opacity="0.3" d="M13 11h10v2H13z" />
                      <path
                        fill={siteInfo.corPrimaria}
                        opacity="0.6"
                        d="M15 13s-6.734 8.106-5.051 9.006C11.633 22.907 15 13 15 13zm6.096 0s6.734 8.105 5.051 9.007c-1.684.9-5.051-9.007-5.051-9.007zM18 13s-2 11 0 11s0-11 0-11z"
                      />
                    </svg>
                    <h3 className="text-xl font-medium mb-2" style={{ color: siteInfo.corSecundaria }}>
                      Mulheres
                    </h3>
                    <p className="text-center" style={{ color: siteInfo.corTerciaria, whiteSpace: "pre-line" }}>
                      {siteInfo.dressCodeMulher}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Local */}
        <section className="mb-16">
          <h2 className="text-3xl font-playfair text-center mb-8" style={{ color: siteInfo.corSecundaria }}>
            Local
          </h2>
          <div className="max-w-6xl mx-auto">{renderLocationSection()}</div>
        </section>

        {/* Como Chegar */}
        <section className="mb-16">
          <h2 className="text-3xl font-playfair text-center mb-8" style={{ color: siteInfo.corSecundaria }}>
            Como Chegar
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="border shadow-md" style={{ borderColor: siteInfo.corTerciaria }}>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Car className="h-5 w-5" style={{ color: siteInfo.corPrimaria }} />
                    <CardTitle className="text-xl" style={{ color: siteInfo.corSecundaria }}>
                      De Carro
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul
                    className="space-y-2 list-disc list-inside"
                    style={{ color: siteInfo.corTerciaria, whiteSpace: "pre-line" }}
                  >
                    <li>Use o aplicativo Waze ou Google Maps para obter a melhor rota</li>
                    <li>Há estacionamento disponível no local</li>
                    <li>Recomendamos chegar com 30 minutos de antecedência</li>
                  </ul>
                  {getMapsLink() && getDirectionsAddress() && (
                    <Link
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(getDirectionsAddress() || '')}&travelmode=driving`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 mt-4 text-sm font-medium"
                      style={{ color: siteInfo.corPrimaria }}
                    >
                      Obter direções <ExternalLink size={14} />
                    </Link>
                  )}
                </CardContent>
              </Card>

              <Card className="border shadow-md" style={{ borderColor: siteInfo.corTerciaria }}>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Bus className="h-5 w-5" style={{ color: siteInfo.corPrimaria }} />
                    <CardTitle className="text-xl" style={{ color: siteInfo.corSecundaria }}>
                      Transporte Público
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul
                    className="space-y-2 list-disc list-inside"
                    style={{ color: siteInfo.corTerciaria, whiteSpace: "pre-line" }}
                  >
                    <li>Verifique as linhas de ônibus que passam próximas ao local</li>
                    <li>Considere usar aplicativos de transporte como Uber ou 99</li>
                    <li>Planeje sua viagem com antecedência para evitar atrasos</li>
                  </ul>
                  {getMapsLink() && getDirectionsAddress() && (
                    <Link
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(getDirectionsAddress() || '')}&travelmode=transit`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 mt-4 text-sm font-medium"
                      style={{ color: siteInfo.corPrimaria }}
                    >
                      Ver rotas de transporte <ExternalLink size={14} />
                    </Link>
                  )}
                </CardContent>
              </Card>

              <Card
                className="border shadow-md md:col-span-2 lg:col-span-1"
                style={{ borderColor: siteInfo.corTerciaria }}
              >
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" style={{ color: siteInfo.corPrimaria }} />
                    <CardTitle className="text-xl" style={{ color: siteInfo.corSecundaria }}>
                      Dicas Importantes
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul
                    className="space-y-2 list-disc list-inside"
                    style={{ color: siteInfo.corTerciaria, whiteSpace: "pre-line" }}
                  >
                    <li>Verifique o clima antes de sair de casa</li>
                    <li>Leve o convite impresso ou digital</li>
                    <li>Chegue com pelo menos 30 minutos de antecedência</li>
                    <li>Em caso de dúvidas, entre em contato conosco</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
